
import { ConferenceConfig, Seat, SystemState } from '../types';

// This replaces direct LocalStorage calls with secure API calls
const API_BASE = '/api';

async function handleResponse(res: Response) {
  const contentType = res.headers.get("content-type");
  
  if (contentType && contentType.indexOf("application/json") === -1) {
    const text = await res.text();
    console.error(`API Error (${res.status}):`, text.substring(0, 200));
    throw new Error(`无法连接到后端 API (Status ${res.status})。请检查网络或部署配置。`);
  }

  const data = await res.json();
  if (!res.ok) {
    // Pass through specific error codes for frontend handling
    if (res.status === 409) {
        throw new Error("CONFLICT: " + (data.error || '数据版本冲突'));
    }
    if (res.status === 429) {
        throw new Error("RATE_LIMIT: " + (data.error || '请求过于频繁'));
    }
    if (res.status === 401 || res.status === 403) {
        throw new Error("AUTH: " + (data.error || '认证失败'));
    }
    throw new Error(data.error || '请求失败');
  }
  return data;
}

export const API = {
  // 1. Get complete system state
  getState: async (): Promise<SystemState> => {
    const res = await fetch(`${API_BASE}?action=getState`);
    return handleResponse(res);
  },

  // 2. Login (Returns Session Token)
  login: async (password: string): Promise<{ success: boolean; token: string }> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'login', password })
    });
    return handleResponse(res);
  },

  // 3. Save Layout (Includes Optimistic Locking & Token Auth)
  saveLayout: async (seats: Seat[], config: ConferenceConfig, token: string, currentVersion: number): Promise<{ success: boolean; newVersion: number }> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'save', seats, config, version: currentVersion })
    });
    return handleResponse(res);
  },

  // 4. Lock System (Token Auth)
  lockSystem: async (token: string): Promise<void> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'lock' })
    });
    return handleResponse(res);
  },

  // 5. Unlock System (Token + TOTP Auth)
  unlockSystem: async (token: string, totpCode: string): Promise<void> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'unlock', totpCode })
    });
    return handleResponse(res);
  },

  // 6. Setup TOTP Init (Token Auth)
  setupTotpInit: async (token: string): Promise<{ secret: string }> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'totp_init' })
    });
    return handleResponse(res);
  },

  // 7. Verify TOTP Setup (Token Auth + 一次性 ticket)
  setupTotpVerify: async (token: string, totpCode: string, secret: string, ticket?: string): Promise<void> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'totp_verify_save', totpCode, secret, ticket })
    });
    return handleResponse(res);
  },

  // 8. Verify TOTP (需登录；成功返回 5 分钟一次性 ticket)
  verifyTotp: async (token: string, totpCode: string): Promise<{ success: boolean; ticket: string }> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'verify_totp', totpCode })
    });
    return handleResponse(res);
  },

  // 9. Change Admin Password (Token Auth)
  changePassword: async (token: string, newPassword: string): Promise<void> => {
    const res = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ action: 'change_password', newPassword })
    });
    return handleResponse(res);
  },
  
  // 10. Reset System (Emergency - Public but protected by TOTP + RateLimit)
  // 中文注释：只把后台密码恢复为 admin996，不再删除座位与会议配置数据。
  resetSystem: async (totpCode: string): Promise<void> => {
      const res = await fetch(`${API_BASE}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'system_reset', totpCode })
      });
      return handleResponse(res);
  },

  // 11. Logout (立即失效服务端会话)
  logout: async (token: string): Promise<void> => {
      if (!token) return;
      try {
        await fetch(`${API_BASE}`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action: 'logout' })
        });
      } catch {
        // 中文注释：登出失败不阻塞前端返回查询页，会话最迟 2 小时后自然过期。
      }
  }
};
